<template>
  <div :class="`form-element form-${type}-element`">
    <FormLabel
      v-if="isWeb || !options.manual_entry"
      :required="options.required"
      :title="options.title"
      :filled="internalValue !== undefined && internalValue !== null"
      :field-type="type"
    />
    <v-row dense>
      <v-col>
        <v-text-field
          v-if="!isWeb && options.manual_entry"
          v-model="internalValue"
          :required="options.required"
          :label="options.title"
          :placeholder="options.placeholder"
          append-outer-icon="mdi-camera"
          hide-details
          @click:append-outer="openScanner"
        >
          <template v-slot:label>
            <FormLabel
              input-label
              :required="options.required"
              :title="options.title"
              :filled="internalValue !== undefined && internalValue !== null"
              :field-type="type"
            />
          </template>
        </v-text-field>
        <v-btn
          v-else-if="!isWeb"
          color="primary"
          block
          dark
          @click="openScanner"
        >
          {{ $t('form.elements.barcode.choose') }}
        </v-btn>
        <v-dialog
          v-else
          v-model="dialog"
          fullscreen
          hide-overlay
          transition="dialog-bottom-transition"
          @input="dialogInput"
        >
          <template v-slot:activator="{ on }">
            <v-btn
              color="primary"
              block
              dark
              v-on="on"
            >
              {{ $t('form.elements.barcode.choose') }}
            </v-btn>
          </template>
          <v-card>
            <v-toolbar
              dark
              color="primary"
            >
              <v-btn
                icon
                dark
                @click="closed"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
              <v-toolbar-title>{{ $t('form.elements.barcode.name') }}</v-toolbar-title>
              <v-spacer />
              <v-toolbar-items>
                <v-btn
                  dark
                  text
                  @click="persist"
                >
                  {{ $t('misc.save') }}
                </v-btn>
              </v-toolbar-items>
            </v-toolbar>
            <v-container>
              <v-text-field
                v-model="result"
                :required="options.required"
                :label="options.title"
                :placeholder="options.placeholder"
              />
              <v-select
                v-model="selectedDeviceId"
                :items="inputSources"
                item-text="text"
                item-value="value"
                :label="$t('form.elements.barcode.camera')"
              />
              <video
                :id="`form-${type}-element-video`"
                width="100%"
                height="100%"
                style="border: 1px solid gray"
              />
            </v-container>
          </v-card>
        </v-dialog>
      </v-col>
      <v-col cols="auto">
        <v-btn
          v-if="zebraScannerAvailable"
          color="primary"
          dark
          @click="openRfidScanner"
        >
          <v-icon>mdi-access-point</v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <small v-if="internalValue && (isWeb || !options.manual_entry)">{{ internalValue }}</small>
  </div>
</template>

<script>
import FormLabel from '../FormLabel';
import { BrowserMultiFormatReader } from '@zxing/library';
import { BarcodeScanner } from '@capacitor-community/barcode-scanner';
import { Scanner } from '@capacitor-community/zebra-scanner';
import { Haptics } from '@capacitor/haptics';

export default {
  components: {
    FormLabel
  },
  props: {
    value: { type: [String, Object], default () { return null; } },
    type: { type: String, required: true },
    options: { type: Object, default: () => ({}) }
  },
  data: () => ({
    dialog: false,
    inputSources: [],
    selectedDeviceId: null,
    result: null,
    codeReader: null,
    isWeb: true,
    zebraScannerAvailable: false
  }),
  computed: {
    internalValue: {
      get () {
        return this.value;
      },
      set (v) {
        this.$emit('input', v);
      }
    }
  },
  watch: {
    selectedDeviceId (newValue, oldValue) {
      if (oldValue) {
        this.stopScan();
        this.startScan();
      }
    }
  },
  mounted () {
    this.isWeb = global.isWeb;

    if (global.isWeb) {
      this.codeReader = new BrowserMultiFormatReader();
      this.codeReader.getVideoInputDevices()
        .then((videoInputDevices) => {
          this.selectedDeviceId = videoInputDevices[0].deviceId;
          if (videoInputDevices.length >= 1) {
            videoInputDevices.forEach((element) => {
              this.inputSources.push({
                text: element.label,
                value: element.deviceId
              });
            });
          }
        })
        .catch((_) => {
          // console.error(err);
        });
    } else {
      Scanner.isAvailable()
        .then((result) => {
          this.zebraScannerAvailable = result.value;
        });
    }
  },
  deactivated() {
    this.stopScanNative();
  },
  beforeDestroy() {
    this.stopScanNative();
  },
  methods: {
    stopScanNative() {
      if (!this.isWeb) {
        document.getElementById('app').classList.remove('barcodescanner-active');
        BarcodeScanner.showBackground();
        BarcodeScanner.stopScan();
      }
    },
    async openScanner () {
      if (this.zebraScannerAvailable) {
        Scanner.startScan().then((result) => {
          this.internalValue = result.value;

          Scanner.stopScan();
          Haptics.vibrate();
        });
      } else {
        const status = await BarcodeScanner.checkPermission({ force: true });
        console.log(status);

        if (!status.granted) {
          return false;
        }

        document.getElementById('app').classList.add('barcodescanner-active');
        BarcodeScanner.startScan().then((result) => {
          if (result.hasContent) {
            this.internalValue = result.content;
            Haptics.vibrate();
          }
          document.getElementById('app').classList.remove('barcodescanner-active');
        });
      }
    },
    openRfidScanner () {
      Scanner.startRfid().then((result) => {
        this.internalValue = result.value;
        Haptics.vibrate();

        Scanner.stopRfid();
      });
    },
    dialogInput (e) {
      if (e) {
        this.opened();
      } else {
        this.closed();
      }
    },
    opened () {
      this.result = this.internalValue;

      this.$nextTick(() => {
        this.startScan();
      });
    },
    closed () {
      this.dialog = false;
      this.stopScan();
    },
    persist () {
      this.internalValue = this.result;
      this.dialog = false;
    },
    startScan () {
      this.codeReader.decodeFromVideoDevice(this.selectedDeviceId, `form-${this.type}-element-video`, (result, err) => {
        if (result) {
          this.result = result.text || this.internalValue;
        }
      });
    },
    stopScan () {
      this.codeReader.reset();
    }
  }
};
</script>
